<template>
  <Teleport to="body">
    <!-- Backdrop -->
    <transition name="modal-fade">
      <div v-show="modelValue" class="fixed inset-0 z-[150] bg-black/60 backdrop-blur-sm" @click="close"></div>
    </transition>

    <!-- Modal Card -->
    <transition name="modal-scale">
      <div v-show="modelValue" class="pointer-events-none fixed inset-0 z-[151] flex items-center justify-center p-4">
        <div
          class="pointer-events-auto max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-800">
          <!-- Header -->
          <div class="flex items-start justify-between gap-4 px-6 pb-4 pt-6">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400">
                {{ brand.cloudName }}
              </p>
              <h3 class="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">
                {{ tt('Services', 'Storitve') }}
              </h3>
              <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {{
                  tt(
                    'The service portals your hub can hand work to. Each one opens in your browser.',
                    'Storitveni portali, ki jim vaše vozlišče lahko preda delo. Vsak se odpre v vašem brskalniku.',
                  )
                }}
              </p>
            </div>
            <button
              @click="close"
              class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200">
              <span class="material-symbols-outlined">close</span>
            </button>
          </div>

          <!-- Launcher grid -->
          <div class="grid grid-cols-1 gap-2 px-6 pb-6 sm:grid-cols-2 lg:grid-cols-3">
            <button
              v-for="portal in SERVICE_PORTALS"
              :key="portal.slug"
              type="button"
              class="group flex items-start gap-3 rounded-xl border border-gray-200 p-3 text-left transition-colors hover:border-primary-400 hover:bg-gray-50 dark:border-gray-700 dark:hover:border-primary-500 dark:hover:bg-gray-700/40"
              @click="open(portal.slug)">
              <span class="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl" :class="portal.tint">
                <span class="material-symbols-outlined text-[24px]">{{ portal.icon }}</span>
              </span>
              <span class="min-w-0 flex-1">
                <span class="flex items-center gap-1 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {{ brand.platformName }} {{ portal.name }}
                  <span
                    class="material-symbols-outlined text-sm text-gray-400 opacity-0 transition-opacity group-hover:opacity-100">
                    open_in_new
                  </span>
                </span>
                <span class="mt-0.5 block text-xs leading-snug text-gray-500 dark:text-gray-400">
                  {{ tt(portal.description.en, portal.description.sl) }}
                </span>
                <span class="mt-1 block truncate text-[11px] text-gray-400 dark:text-gray-500">
                  {{ servicePortalHost(portal.slug) }}
                </span>
              </span>
            </button>
          </div>

          <!-- Footer -->
          <div class="flex items-center justify-between gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
            <p class="text-xs text-gray-500 dark:text-gray-400">
              {{
                tt(
                  'Businesses run each portal — you use them from here.',
                  'Vsak portal vodijo podjetja — vi jih uporabljate od tukaj.',
                )
              }}
            </p>
            <button
              type="button"
              class="inline-flex items-center gap-1 text-xs font-semibold text-gray-600 transition-colors hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
              @click="openSite">
              {{ siteHost }}
              <span class="material-symbols-outlined text-sm">open_in_new</span>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup lang="ts">
import { brand } from '../../config/brand';
import { openExternalUrl } from '../../services/externalLink';
import { SERVICE_PORTALS, servicePortalHost, servicePortalUrl } from '../../config/servicePortals';
import { tt } from '../../utils/consoleText';

defineProps<{
  modelValue: boolean;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
}>();

/** Wording goes through `tt(en, sl)`, like the rest of the servers shell:
 *  the copy is inline English and Slovenian is the business side's first
 *  language. */
const siteHost = brand.siteUrl.replace(/^https?:\/\//, '');

function close() {
  emit('update:modelValue', false);
}

/** A portal leaves the app — Electron and Capacitor both need their own call. */
function open(slug: string) {
  close();
  void openExternalUrl(servicePortalUrl(slug));
}

function openSite() {
  close();
  void openExternalUrl(brand.siteUrl);
}
</script>

<style scoped>
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-scale-enter-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}
.modal-scale-leave-active {
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}
.modal-scale-enter-from {
  opacity: 0;
  transform: scale(0.95);
}
.modal-scale-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
</style>
